import React, { ReactElement, useContext } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
} from 'recharts';
import { SingleValueNode, NodeWithGroupInfo } from '../../../types/chartDataTypes';
import { LocaleContext } from '../../../shared/context/LocaleContext';
import {
  baseColor, maxOpacity, Container, InnerLabel,
} from './PieChart.styles';
import theme, { toRgba } from '../../../shared/theme';

export type OnCellEventFunction = (dataName: string) => void;

export type PieChartProps = {
  data: SingleValueNode,
  innerLabel?: string,
  highlightDataName?: string,
  onDataHover?: OnCellEventFunction,
  onDataClick?: OnCellEventFunction,
  hideLabel?: boolean,
};

const CustomPieChart = ({
  data,
  innerLabel,
  highlightDataName,
  onDataHover,
  onDataClick,
  hideLabel,
}: PieChartProps): ReactElement => {
  const { formatter } = useContext(LocaleContext);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx, cy, midAngle, outerRadius, percent,
  }): SVGElement => {
    if (percent < 0.02) {
      // hide label if it does not nearly fit into the slice
      return null;
    }

    const radius = outerRadius * 0.80;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // @ts-ignore
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: 'none' }}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) as SVGElement;
  };

  // ATTENTION: A Pie Chart cannot display negative values!
  // HINT: We assume that all values are either positive or negative (no mixed values!)
  const getAbsValue = (entry: SingleValueNode): number => Math.abs(entry.value);

  return (
    <Container>
      <ResponsiveContainer width="100%" height={270}>
        <PieChart width={1000} height={400}>
          <foreignObject
            x="0"
            y="0"
            width="100%"
            height="100%"
          >
            <InnerLabel>
              {innerLabel}
              <br />
              {formatter.formatNumber(data.value)}
            </InnerLabel>
          </foreignObject>
          <Pie
            dataKey={getAbsValue}
            isAnimationActive
            data={data.entries}
            innerRadius="60%"
            outerRadius="100%"
            fill="#8884d8"
            blendStroke={false}
            paddingAngle={0}
            startAngle={90}
            endAngle={-270}
            minAngle={2}
            labelLine={false}
            label={hideLabel ? undefined : renderCustomizedLabel}
          >
            {data.entries.map((entry, index) => {
              const dataName = entry.totalLabel || entry.name;
              const groupNames = (entry as NodeWithGroupInfo).groupNames || [];
              const fillBaseColor = highlightDataName
                && (highlightDataName === dataName || groupNames.includes(highlightDataName))
                ? theme.colors.lightGreen
                : baseColor;
              const fillColor = highlightDataName === dataName
                ? fillBaseColor
                : toRgba(fillBaseColor, 1 - (((1 - maxOpacity) / (data.entries.length - 1)) * index));
              const onHoverCell = (): void => {
                if (onDataHover) {
                  onDataHover(dataName);
                }
              };
              const onLeaveCell = (): void => {
                if (onDataHover) {
                  onDataHover(null);
                }
              };
              const onClickCell = (): void => {
                if (onDataClick) {
                  onDataClick(dataName);
                }
              };
              return (
                <Cell
                  key={`cell-${entry.name}`}
                  fill={fillColor}
                  stroke=""
                  onMouseOver={onHoverCell}
                  onMouseOut={onLeaveCell}
                  onClick={onClickCell}
                  style={{ cursor: 'pointer' }}
                />
              );
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </Container>
  );
};

export default CustomPieChart;
